import React, { useEffect, useState } from 'react';
import {
  CommonButton,
  CommonOutlinedButton,
  CustomCityStateTextField,
  CustomLeftIcon,
  CustomMenuItem,
  CustomRequiredInputLabel,
  CustomSelect,
  CustomTextFeild,
} from '../../common/StyledComponents/StyledFields';
import { Box, FormControl, Grid, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import styles from './vendor.module.scss';
import { ValidationError, object, string } from 'yup';
import { apiRequest, catchErrorMessageHandler } from '../../../infrastructure/backendService';
import { isEmpty, isNil } from 'lodash';
import apiPath from '../../../config/apiPaths.json';
import ErrorPopup from '../../common/ErrorPopup';
import toast, { Toaster } from "react-hot-toast";
import commonStyles from '../../../App.module.scss';
import { ClipLoader } from 'react-spinners';
import SuccessPopup from '../../devotee/donation/successPopup';
import LOCALE from '../../../config/en-us.json';
import constants from '../../../config/constants.json';

interface VendorProps {
  selectData: any
  handleBack?: () => void;
}

interface ErrorMessageTypes {
  vendorName: string;
  zipcode: string;
  phoneNumber: string;
  email: string;
  state: string;
  paymentMethod: string;
  address: string;
  service: string;
  fax: string;
  city: string;
}
const VendorsForm = (props: VendorProps) => {
  const { selectData } = props || "";
  const formFields = {
    vendorName: '',
    zipcode: '',
    phoneNumber: '',
    email: '',
    state: '',
    paymentMethod: '',
    address: '',
    service: '',
    fax: '',
    city: '',
  };
  const [formData, setFormData] = useState({
    ...formFields,
  });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [addloader, setAddloader] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ErrorMessageTypes>({
    ...formFields,
  });
  const [statesList, setStatesList] = useState<any[]>([]);
  const [zipcodeError, setZipcodeError] = useState("");
  const validationSchema = object().shape({
    vendorName: string().required('Vendor Name is Required'),
    zipcode: string().
      trim()
      .required('ZipCode is Required')
      .matches(/^\d{5}(-\d{4})?$/, "Invalid zipcode"),
    phoneNumber: string().required('Phone Number is Required'),
    email: string().required('Email is Required'),
    city: string().required('City is Required'),
    address: string().required('Address is Required'),
    fax: string().required('Fax is Required'),
    paymentMethod: string().required('Payment Method is Required'),
    service: string().required('Utility/Commodity/Service is Required'),
    state: string().required('State is Required'),
  });


  const zipCodeApi = () => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.cityAndStateApi,
      params: {
        zip: formData.zipcode,
      },
    })
      .then((res) => {
        setFormData({
          ...formData,
          state: res.data.stateName,
          city: res.data.city,
        });
        setZipcodeError('');
      })
      .catch((err) => {
        setErrorMessage({
          ...errorMessage,
          zipcode: catchErrorMessageHandler(err),
        });
        setFormData({
          ...formData,
          state: "",
          city: "",
        });
      });
  };
  const saveApiCall = () => {
    setAddloader(true);
    const reqBody = {
      vendorName: formData.vendorName,
      phone: formData.phoneNumber,
      email: formData.email,
      state: formData.state,
      items: formData.service,
      zipcode: formData.zipcode,
      address: formData.address,
      city: formData.city,
      fax: formData.fax,
      paymentMethod: formData.paymentMethod,
      country: "USA",
    };
    apiRequest({
      method: 'POST',
      endpoint: apiPath.templeVendors,
      body: JSON.stringify(reqBody),
      includeToken: true,
    })
      .then((res) => {
        const successMessage = LOCALE.VENDOR_ADDED;
        toast.custom(
          <SuccessPopup
            headerMessage="Details"
            showMessage={successMessage}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: 3000,
          }
        );
        setAddloader(false);
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup
            showMessage={catchErrorMessageHandler(err)}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
      setAddloader(false);
  };

  const handleSave = async (): Promise<void> => {
    const errors: ErrorMessageTypes = { ...formFields };

    try {
      // Validate the form data against the validation schema
      await validationSchema.validate(formData, { abortEarly: false });
      // If validation passes, you can submit the form
      setErrorMessage(errors);
      // Call your API or perform other actions here
      saveApiCall();
    } catch (error: any) {
      // Handle validation errors

      if (error instanceof ValidationError) {
        error.inner.forEach((e: any) => {
          if (e.path) {
            errors[e.path as keyof ErrorMessageTypes] = e.message;
          }
        });
      }
      setErrorMessage(errors);
    }
  };
  const handleFieldChange = (field: string, value: any) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const paymentMethod = {
    check: "check",
    cash: "cash"
  }


  const StateDropdown = (): React.ReactElement => {
    return (
      <FormControl sx={{ width: '100%' }}>
        <CustomSelect
          IconComponent={KeyboardArrowDownIcon}
          defaultValue=""
          id="grouped-select"
          value={formData.state}
          onChange={(e) => {
            handleFieldChange("state", e.target.value);
            setErrorMessage({ ...errorMessage, state: "" })
          }
          }
          className={errorMessage.state ? styles.errorTextField : styles.dropdownStyle}
          MenuProps={{
            PaperProps: {
              sx: {
                border: '1px solid #F05802',
                '& ul': {
                  paddingTop: 0,
                  paddingBottom: 0,
                  overflowY: 'auto',
                  minHeight: 'auto',
                  maxHeight: '300px',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#F3C539',
                    borderRadius: '6px',
                  },
                },
              },
            },
          }}
        >
          {statesList.map((item: any, index) => (
            <CustomMenuItem key={index} value={item.stateName}>
              {item.stateName}
            </CustomMenuItem>
          ))}
        </CustomSelect>
        {!isEmpty(errorMessage.state) && (
          <Typography sx={{ color: 'red', fontSize: '12px' }}>{errorMessage.state}</Typography>
        )}
      </FormControl>
    );
  };
  const fetchAllStatesList = () => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.findAllStates,
      includeToken: true,
      params: {
        size: 500,
        sortBy: "stateName",
      },
    })
      .then((res) => {
        setStatesList(res?.data?.content);
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  useEffect(() => {
    fetchAllStatesList();
  }, []);
  useEffect(() => {
    updateData();
  }, [selectData]);

  const updateData = () => {
    setFormData({
      vendorName: selectData?.vendorName || "",
      email: selectData?.email || "",
      fax: selectData?.fax || "",
      phoneNumber: selectData?.phone || "",
      address: selectData?.address || "",
      service: selectData?.items || "",
      paymentMethod: selectData?.paymentMethod || "",
      zipcode: selectData?.zipcode || "",
      city: selectData?.city || "",
      state: selectData?.state || ""
    })
  };
  const handleUpdate = async (): Promise<void> => {
    const errors: ErrorMessageTypes = { ...formFields };
    try {
      // Validate the form data against the validation schema
      await validationSchema.validate(formData, { abortEarly: false });
      // If validation passes, you can submit the form
      setErrorMessage(errors);
      // Call your API or perform other actions here
      updateVendorApiCall();
    } catch (error: any) {
      // Handle validation errors
      if (error instanceof ValidationError) {
        error.inner.forEach((e: any) => {
          if (e.path) {
            errors[e.path as keyof ErrorMessageTypes] = e.message;
          }
        });
      }
      setErrorMessage(errors);
    }
  };
  const updateVendorApiCall = () => {
    setSubmitLoading(true);
    const reqBody = {
      vendorName: formData?.vendorName,
      email: formData?.email,
      fax: formData?.fax,
      phone: formData?.phoneNumber,
      address: formData?.address,
      items: formData?.service,
      paymentMethod: formData?.paymentMethod,
      zipcode: formData?.zipcode,
      city: formData?.city,
      state: formData?.state,
      country: "USA",
      id: selectData?.id,
    };
    apiRequest({
      method: 'PUT',
      endpoint: `${apiPath.templeVendors}/${selectData?.id}`,
      body: JSON.stringify(reqBody),
      includeToken: true,
    })
      .then((res) => {
        const successMessage = LOCALE.VENDOR_UPDATED;
        toast.custom(
          <SuccessPopup
            headerMessage="Details Update"
            showMessage={successMessage}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          },
        );
        setSubmitLoading(false);
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup
            showMessage={catchErrorMessageHandler(err)}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          },
        );
        setSubmitLoading(false);
      });
  };

  const validateVendorName = (value: string) => {
    const regex = /^[a-zA-Z0-9\s]*$/;
    return regex.test(value);
 };
  const paymentMethodDropdown = (): React.ReactElement => {
    return (
      <FormControl sx={{ width: '100%' }}>
        <CustomSelect
          IconComponent={KeyboardArrowDownIcon}
          defaultValue=""
          id="grouped-select"
          value={formData.paymentMethod}
          onChange={(e) => {
            handleFieldChange('paymentMethod', e.target.value);
            setErrorMessage({ ...errorMessage, paymentMethod: '' });
          }}
          className={errorMessage.paymentMethod ? styles.errorTextField : styles.dropdownStyle}
          MenuProps={{
            PaperProps: {
              sx: {
                border: '1px solid #F05802',
                '& ul': {
                  paddingTop: 0,
                  paddingBottom: 0,
                  overflowY: 'auto',
                  minHeight: 'auto',
                  maxHeight: '300px',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#F3C539',
                    borderRadius: '6px',
                  },
                },
              },
            },
          }}
        >
          {Object.entries(paymentMethod).map(([key, value]) => (
            <CustomMenuItem key={key} value={key}>
              {value}
            </CustomMenuItem>
          ))}
        </CustomSelect>
        {!isEmpty(errorMessage.paymentMethod) && (
          <Typography sx={{ color: 'red', fontSize: '12px' }}>
            {errorMessage.paymentMethod}
          </Typography>
        )}
      </FormControl>
    );
  };
  const handleReset = () => {
    updateData();
  }
  const handleCancel = () => {
    setFormData({
      ...formFields
    })
  }
  const handleback = () => {
    props.handleBack?.();
};
  const handleButtons = () => {
    return (
      <div>
        {!isNil(selectData?.id) ? (
          <>
            <button className={commonStyles.cancelButton} onClick={handleReset}>
              Reset
            </button>
            <CommonButton className={commonStyles.saveButton} onClick={handleUpdate}>
              {submitLoading ? (
                <>
                  <ClipLoader size={20} color="#fff" />
                  <span style={{ marginLeft: '8px' }}>Update</span>
                </>
              ) : (
                'Update'
              )}
            </CommonButton>
          </>
        ) : (
          <>
            <button className={commonStyles.cancelButton} onClick={handleCancel}>
              Reset
            </button>
            <CommonButton className={commonStyles.saveButton} onClick={handleSave}>
              {addloader ? (
                <>
                  <ClipLoader size={20} color="#fff" />
                  <span style={{ marginLeft: '8px' }}>Add</span>
                </>
              ) : (
                'Add'
              )}
            </CommonButton>
          </>
        )}
      </div>
    );
  };
  const validateEmail = () => {
    const { email } = formData;

    if (email.length < 6) {
      setErrorMessage({
        ...errorMessage,
        email: "Email must be at least 6 characters long.",
      });
    } else if (email.length >= 64) {
      setErrorMessage({
        ...errorMessage,
        email: "Email must be less than 64 characters long.",
      });
    } else if (!email.includes("@") || !email.includes(".")) {
      setErrorMessage({
        ...errorMessage,
        email: "Please Enter Valid Email",
      });
    } else {
      setErrorMessage({ ...errorMessage, email: "" });
    }
  };
  const handleFaxChange = (e:any) => {
    let value = e.target.value;

    // Validate if value contains only numeric characters and is within 25 characters
    if (/^\d*$/.test(value) && value.length <= 25) {
      handleFieldChange("fax", value);
      setErrorMessage({ ...errorMessage, fax: '' }); // Clear error message
    } else {
      setErrorMessage({ ...errorMessage, fax: 'Fax should contain only numeric values and be max 25 characters.' });
    }
  };

  return (
    <div className={styles.mainContainer}>
       <Grid sx={{ display: "flex" }} onClick={handleback}>
        <span style={{
          marginTop: "4px",
          marginRight: "4px",
        }}>
          <CustomLeftIcon/>
        </span>
        <Typography component="div" className={styles.typoGraphyHeading}> Add New Vendor</Typography>
      </Grid>
    <div className={styles.formLayout}>
      <Toaster />
   
      <Grid className={styles.formContainer}>
        <Grid container spacing={2} sx={{ marginTop: '0px' }}>
          <Grid item xs={12} sm={3}>
            {CustomRequiredInputLabel('Vendor Name', true)}
            <CustomTextFeild
              fullWidth
              required
              value={formData.vendorName}
              onChange={(e) => {
                const value = e.target.value;
                if (validateVendorName(value)) {
                   setErrorMessage({ ...errorMessage, vendorName: '' });
                   handleFieldChange('vendorName', value);
                }
             }}
              className={errorMessage.vendorName ? styles.errorTextField : styles.textField}
              placeholder="Vendor Name"
            />
            {!isEmpty(errorMessage.vendorName) && (
              <Typography sx={{ color: 'red', fontSize: '12px' }}>
                {errorMessage.vendorName}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            {CustomRequiredInputLabel('Zip Code', true)}
            <CustomTextFeild
              fullWidth
              required
              value={formData.zipcode}
              onChange={(e) => {
                const inputValue = e.target.value;
                // Only allow numbers
                const sanitizedInput = inputValue.replace(/[^0-9]/g, '').slice(0, 10);
                handleFieldChange('zipcode', sanitizedInput);
                setErrorMessage({ ...errorMessage, zipcode: '' });
              }}
              className={styles.textField}
              placeholder="Enter Zipcode"
              onBlur={zipCodeApi}
            />
            {!isEmpty(errorMessage.zipcode) || zipcodeError && (
              <Typography sx={{ color: 'red', fontSize: '12px' }}>
                {errorMessage.zipcode || zipcodeError}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            {CustomRequiredInputLabel('City', true)}
            <CustomTextFeild
              fullWidth
              required
              value={formData.city}
              onChange={(e) => {
                handleFieldChange('city', e.target.value);
                setErrorMessage({ ...errorMessage, city: '' });
              }}
              className={errorMessage.city ? styles.errorTextField : styles.textField}
            />
            {!isEmpty(errorMessage.city) && (
              <Typography sx={{ color: 'red', fontSize: '12px' }}>
                {errorMessage.city}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ marginTop: '0px' }}>
          <Grid item xs={12} sm={3}>
            {CustomRequiredInputLabel('State', true)}
            {StateDropdown()}
          </Grid>
          <Grid item xs={12} sm={3}>
            {CustomRequiredInputLabel('Address', true)}
            <CustomTextFeild
              fullWidth
              required
              value={formData.address}
              onChange={(e: any) => {
                const inputValue = e.target.value;
                if (inputValue.length <= 100) {
                  setErrorMessage({ ...errorMessage, address: "" });
                  handleFieldChange("address", inputValue);
                } else {
                  setErrorMessage({
                    ...errorMessage,
                    address: "Address cannot exceed 100 characters",
                  });
                }
              }}
              className={errorMessage.address ? styles.errorTextField : styles.textField}
              placeholder="Enter Address"
            />
            {!isEmpty(errorMessage.address) && (
              <Typography sx={{ color: 'red', fontSize: '12px' }}>
                {errorMessage.address}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            {CustomRequiredInputLabel('Phone', true)}
            <CustomTextFeild
              fullWidth
              required
              value={formData.phoneNumber}
              onChange={(e) => {
                const numericValue = e.target.value.replace(/[^0-9]/g, ''); // Only allow numeric values
                const slicedNumber = numericValue.slice(0, 10);
                handleFieldChange('phoneNumber',slicedNumber);
                setErrorMessage({ ...errorMessage, phoneNumber: '' });
              }}
              className={errorMessage.phoneNumber ? styles.errorTextField : styles.textField}
              placeholder="Phone Number"
            />
            {!isEmpty(errorMessage.phoneNumber) && (
              <Typography sx={{ color: 'red', fontSize: '12px' }}>
                {errorMessage.phoneNumber}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ marginTop: '0px' }}>

          <Grid item xs={12} sm={3}>
            {CustomRequiredInputLabel('Fax', true)}
            <CustomTextFeild
              fullWidth
              required
              value={formData.fax}
              onChange={handleFaxChange}
              className={errorMessage.fax ? styles.errorTextField : styles.textField}
              placeholder="Fax"
            />
            {!isEmpty(errorMessage.fax) && (
              <Typography sx={{ color: 'red', fontSize: '12px' }}>
                {errorMessage.fax}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            {CustomRequiredInputLabel('Email', true)}
            <CustomTextFeild
              fullWidth
              required
              value={formData.email}
              onChange={(e) => {
                handleFieldChange('email', e.target.value);
                setErrorMessage({ ...errorMessage, email: '' });
              }}
              className={errorMessage.email ? styles.errorTextField : styles.textField}
               placeholder="Email"
               onBlur={validateEmail}
            />
            {!isEmpty(errorMessage.email) && (
              <Typography sx={{ color: 'red', fontSize: '12px' }}>
                {errorMessage.email}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            {CustomRequiredInputLabel('Utility/Commodity/Service', true)}
            <CustomTextFeild
              fullWidth
              required
              value={formData.service}
              onChange={(e) => {
                handleFieldChange('service', e.target.value);
                setErrorMessage({ ...errorMessage, service: '' });
              }}
              className={errorMessage.city ? styles.errorTextField : styles.textField}
               placeholder="Utility/Commodity/Service"
            />
            {!isEmpty(errorMessage.service) && (
              <Typography sx={{ color: 'red', fontSize: '12px' }}>
                {errorMessage.service}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ marginTop: '0px' }}>

          <Grid item xs={12} sm={3}>
            {CustomRequiredInputLabel('Payment Method', true)}
            {paymentMethodDropdown()}
          </Grid>
        </Grid>
      </Grid>
      <Box sx={{ marginTop: '20px' }}>
        <Grid sx={{ display: 'flex', gap: '10px' }}>
          {handleButtons()}
        </Grid>
      </Box>
    </div>
    </div>
  );
};
export default VendorsForm;
